import React, { useState, useEffect, useRef } from 'react';
import './ProjectDropdown.css';
// @ts-ignore
import checkmarkIcon from '../../image-assets/icon.checkmark.svg';
// @ts-ignore
import addIcon from '../../image-assets/icon.add.svg';
// @ts-ignore
// import editIcon from '../../image-assets/icon.edit.svg'; // Remove edit icon import
// @ts-ignore
import settingsIcon from '../../image-assets/icon.settings.svg'; // Import settings icon
import { getRole, getPermissions } from '../../lib/permissions';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';

// Add TestRole type
type TestRole = 'owner' | 'admin' | 'reviewer';

interface ProjectDropdownProps {
  isOpen: boolean;
  items: {
    teams: Array<{
      id: string;
      name: string;
      projects: Array<{
        id: string;
        name: string;
      }>;
    }>;
  };
  position?: 'left' | 'right';
  onSelect: (projectId: string) => void;
  onTeamSelect: (teamId: string) => void;
  onCreateProject: (teamId: string) => void;
  onEditProject: (projectId: string, name: string) => void;
  onDeleteProject: (projectId: string) => Promise<void>;
  onCreateTeam: () => void;
  selectedProjectId?: string;
  testRole?: TestRole;
}

const ProjectDropdown: React.FC<ProjectDropdownProps> = ({
  isOpen,
  items,
  position = 'left',
  onSelect,
  onTeamSelect,
  onCreateProject,
  onEditProject,
  onDeleteProject,
  onCreateTeam,
  selectedProjectId,
  testRole
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { teams } = useWorkspace();

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // When closing, wait for animation to complete before removing from DOM
      const menu = menuRef.current;
      if (menu) {
        menu.style.animation = 'dropdownClose 0.12s ease-in forwards';
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 120); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen]);

  const getTeamPermissions = (teamId: string) => {
    // If test role is provided, use it directly
    if (testRole) {
      return getPermissions(testRole, testRole === 'owner');
    }

    // Otherwise, get the actual role for the team
    const team = teams.find(t => t.id === teamId);
    if (!team || !user || !profileData) return null;
    
    const role = getRole(team, user.id, profileData.id);
    return getPermissions(role, role === 'owner');
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={menuRef}
      className={`dropdown-menu project-selector-dropdown ${position === 'right' ? 'dropdown-menu-right' : ''}`}
    >
      <div className="dropdown-content">
        {[...items.teams]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((team) => {
            const teamPermissions = getTeamPermissions(team.id);
            return (
              <div key={team.id} className="dropdown-section">
                <div className="team-item">
                  <button 
                    className="team-button"
                    onClick={() => onTeamSelect(team.id)}
                  >
                    {team.name}
                  </button>
                  {teamPermissions?.canManageProjects && (
                    <img 
                      src={addIcon} 
                      alt="Add project" 
                      className="dropdown-item-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateProject(team.id);
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </div>
                {[...team.projects]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((project) => {
                    return (
                      <button
                        key={project.id}
                        className={`dropdown-item project-item ${selectedProjectId === project.id ? 'selected' : ''}`}
                        onClick={() => onSelect(project.id)}
                      >
                        <span>{project.name}</span>
                        <div className="project-actions">
                          {/* Always render checkmark, visibility controlled by CSS */}
                          <img 
                            src={checkmarkIcon} 
                            alt="Selected" 
                            className="check-icon"
                            // style={{ border: '2px solid red' }} // Remove temporary debug border
                          />
                          {teamPermissions?.canManageProjects && (
                            <img 
                              src={settingsIcon} // Use settings icon
                              alt="Project settings" // Update alt text
                              className="edit-icon" // Keep class name for styling logic
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onEditProject(project.id, project.name);
                              }}
                            />
                          )}
                        </div>
                      </button>
                    );
                  })}
              </div>
            );
          })}
      </div>
      <div className="dropdown-footer">
        <button className="new-team-button" onClick={onCreateTeam}>
          <img src={addIcon} alt="Add team" />
          <span>New Team</span>
        </button>
      </div>
    </div>
  );
};

export default ProjectDropdown; 